import { PageTitle as SharedPageTitle } from '../../../components/layout/PageHeader';
import React from 'react';
import { QrCode } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button } from '../../../components/ui/Button';
import { CreateQrShareModal } from '../../qr-shares/components/CreateQrShareModal';
import { QrSharesListView } from '../../qr-shares/components/QrSharesListView';
import { SmartSortingView } from '../../smart-sorting/components/SmartSortingView';
import { UserFilesView } from './UserFilesView';

const Container = styled.div`
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
`;

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-md);
  margin-bottom: var(--space-lg);
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const TabList = styled.div`
  display: flex;
  gap: var(--space-lg);
  margin-bottom: var(--space-xl);
  overflow-x: auto;
  border-bottom: 1px solid var(--border);
`;

const Tab = styled.button<{ $selected: boolean }>`
  min-height: 44px;
  margin-bottom: -1px;
  padding: var(--space-sm) var(--space-xs);
  border: 0;
  border-bottom: 3px solid
    ${(props) => (props.$selected ? 'var(--brand-600)' : 'transparent')};
  background: transparent;
  color: ${(props) =>
    props.$selected ? 'var(--brand-700)' : 'var(--text-secondary)'};
  font: inherit;
  font-weight: ${(props) =>
    props.$selected
      ? 'var(--font-weight-semibold)'
      : 'var(--font-weight-medium)'};
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    color: var(--brand-700);
    border-bottom-color: ${(props) =>
      props.$selected ? 'var(--brand-600)' : 'var(--brand-200)'};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
  }
`;

const TabPanel = styled.section`
  min-width: 0;

  &[hidden] {
    display: none;
  }
`;

const FILE_VIEWS = ['classification', 'all', 'qr-shares'] as const;
type FilesView = (typeof FILE_VIEWS)[number];

const SUBTITLES: Record<FilesView, string> = {
  classification: 'Sube documentos y revisa su clasificación.',
  all: 'Busca y descarga todos los documentos asociados a tu cuenta.',
  'qr-shares':
    'Gestiona los enlaces y códigos QR que has compartido con terceros.',
};

const TAB_IDS: Record<FilesView, string> = {
  all: 'files-tab-all',
  classification: 'files-tab-classification',
  'qr-shares': 'files-tab-qr-shares',
};

const PANEL_IDS: Record<FilesView, string> = {
  all: 'files-panel-all',
  classification: 'files-panel-classification',
  'qr-shares': 'files-panel-qr-shares',
};

function parseFilesView(value: string | null): FilesView {
  return FILE_VIEWS.includes(value as FilesView)
    ? (value as FilesView)
    : 'classification';
}

export const FilesHubView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isShareModalOpen, setIsShareModalOpen] = React.useState(false);
  const activeView: FilesView = parseFilesView(searchParams.get('view'));

  const handleViewChange = React.useCallback(
    (view: FilesView) => {
      const nextParams = new URLSearchParams(searchParams);
      if (view === 'classification') {
        nextParams.delete('view');
      } else {
        nextParams.set('view', view);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const handleTabKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, currentView: FilesView) => {
      const currentIndex = FILE_VIEWS.indexOf(currentView);
      let nextView: FilesView | null = null;

      if (event.key === 'ArrowRight') {
        nextView = FILE_VIEWS[(currentIndex + 1) % FILE_VIEWS.length] ?? null;
      } else if (event.key === 'ArrowLeft') {
        nextView =
          FILE_VIEWS[
            (currentIndex - 1 + FILE_VIEWS.length) % FILE_VIEWS.length
          ] ?? null;
      } else if (event.key === 'Home') {
        nextView = FILE_VIEWS[0] ?? null;
      } else if (event.key === 'End') {
        nextView = FILE_VIEWS[FILE_VIEWS.length - 1] ?? null;
      }

      if (!nextView) return;

      event.preventDefault();
      handleViewChange(nextView);
      document.getElementById(TAB_IDS[nextView])?.focus();
    },
    [handleViewChange],
  );

  return (
    <Container>
      <Header>
        <HeaderText>
          <Title>Archivos</Title>
          <Subtitle aria-live="polite">{SUBTITLES[activeView]}</Subtitle>
        </HeaderText>
        <Button
          variant="primary"
          startIcon={<QrCode size={18} aria-hidden="true" />}
          onClick={() => setIsShareModalOpen(true)}
        >
          Compartir por QR
        </Button>
      </Header>

      <TabList role="tablist" aria-label="Vistas de archivos">
        <Tab
          id={TAB_IDS.classification}
          type="button"
          role="tab"
          aria-selected={activeView === 'classification'}
          aria-controls={PANEL_IDS.classification}
          tabIndex={activeView === 'classification' ? 0 : -1}
          $selected={activeView === 'classification'}
          onClick={() => handleViewChange('classification')}
          onKeyDown={(event) => handleTabKeyDown(event, 'classification')}
        >
          Clasificación
        </Tab>
        <Tab
          id={TAB_IDS.all}
          type="button"
          role="tab"
          aria-selected={activeView === 'all'}
          aria-controls={PANEL_IDS.all}
          tabIndex={activeView === 'all' ? 0 : -1}
          $selected={activeView === 'all'}
          onClick={() => handleViewChange('all')}
          onKeyDown={(event) => handleTabKeyDown(event, 'all')}
        >
          Todos los archivos
        </Tab>
        <Tab
          id={TAB_IDS['qr-shares']}
          type="button"
          role="tab"
          aria-selected={activeView === 'qr-shares'}
          aria-controls={PANEL_IDS['qr-shares']}
          tabIndex={activeView === 'qr-shares' ? 0 : -1}
          $selected={activeView === 'qr-shares'}
          onClick={() => handleViewChange('qr-shares')}
          onKeyDown={(event) => handleTabKeyDown(event, 'qr-shares')}
        >
          QR compartidos
        </Tab>
      </TabList>

      <TabPanel
        id={PANEL_IDS.all}
        role="tabpanel"
        aria-labelledby={TAB_IDS.all}
        hidden={activeView !== 'all'}
      >
        <UserFilesView embedded />
      </TabPanel>
      <TabPanel
        id={PANEL_IDS.classification}
        role="tabpanel"
        aria-labelledby={TAB_IDS.classification}
        hidden={activeView !== 'classification'}
      >
        <SmartSortingView embedded />
      </TabPanel>
      <TabPanel
        id={PANEL_IDS['qr-shares']}
        role="tabpanel"
        aria-labelledby={TAB_IDS['qr-shares']}
        hidden={activeView !== 'qr-shares'}
      >
        {/* Mounted only while selected so its queries do not run on other tabs. */}
        {activeView === 'qr-shares' && <QrSharesListView />}
      </TabPanel>

      {/* Mounted on demand so the hub does not run the modal's queries while closed. */}
      {isShareModalOpen && (
        <CreateQrShareModal isOpen onClose={() => setIsShareModalOpen(false)} />
      )}
    </Container>
  );
};
